import { useEffect, useState } from "react";
import { ApiError, api } from "../../api/client";
import { cancelReasonLabel } from "../../constants/appointmentCancelReasons";
import { lessonTypeShort } from "./WeeklyCalendar";
import "./AuditHistoryModal.css";

export type AuditEntry = {
  id: string;
  action_type: string;
  actor_user_id: string;
  actor_display_name?: string | null;
  before_data: Record<string, unknown> | null;
  after_data: Record<string, unknown> | null;
  created_at: string;
};

type AuditResponse = {
  appointment_id: string;
  total: number;
  labels?: Record<string, string>;
  entries: AuditEntry[];
};

type Props = {
  appointmentId: string;
  onClose: () => void;
};

const ACTION_LABELS: Record<string, { label: string; tone: "info" | "warn" | "danger" | "success" }> = {
  appointment_created: { label: "Randevu oluşturuldu", tone: "success" },
  appointment_conflict_override_accepted: {
    label: "Çakışmaya rağmen istisna ile oluşturuldu",
    tone: "warn",
  },
  appointment_conflict_override_reverted: {
    label: "Çakışma istisnası geri alındı",
    tone: "info",
  },
  appointment_moved: { label: "Randevu taşındı", tone: "info" },
  appointment_moved_with_override: {
    label: "Çakışmaya rağmen istisna ile taşındı",
    tone: "warn",
  },
  appointment_replanned: { label: "Randevu yeniden planlandı", tone: "info" },
  appointment_cancelled: { label: "Randevu İptal Edildi", tone: "danger" },
  appointment_reactivated: { label: "Randevu yeniden aktifleştirildi", tone: "success" },
  appointment_deleted: { label: "Randevu silindi", tone: "danger" },
};

const STATUS_LABELS: Record<string, string> = {
  planned: "Planlandı",
  in_progress: "Devam ediyor",
  completed: "Tamamlandı",
  cancelled: "İptal",
};

function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });
}

function shortUuid(value: string | null | undefined): string {
  if (!value) return "-";
  if (value.length <= 8) return value;
  return value.slice(0, 8);
}

function formatRefValue(key: string, val: unknown, labels: Record<string, string>): string {
  if (val === null || val === undefined || val === "") return "-";
  if (key === "starts_at" || key === "ends_at") {
    return formatDateTime(String(val));
  }
  if (
    key === "instructor_id" ||
    key === "student_id" ||
    key === "vehicle_id" ||
    key === "actor_user_id" ||
    key === "conflict_accepted_by" ||
    key === "created_by" ||
    key === "updated_by"
  ) {
    const id = String(val);
    return labels[id] || shortUuid(id);
  }
  if (key === "lesson_type") {
    return lessonTypeShort(String(val));
  }
  if (key === "status") {
    return STATUS_LABELS[String(val)] || String(val);
  }
  if (key === "cancel_reason_code") {
    return cancelReasonLabel(String(val));
  }
  if (typeof val === "boolean") return val ? "Evet" : "Hayır";
  return String(val);
}

function asAuditRecord(data: unknown): Record<string, unknown> | null {
  if (data === null || data === undefined) return null;
  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data) as unknown;
      return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  if (typeof data === "object" && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  return null;
}

function describeChange(before: unknown, after: unknown, labels: Record<string, string>): string[] {
  const beforeRec = asAuditRecord(before);
  const afterRec = asAuditRecord(after);
  const lines: string[] = [];
  const fields: Array<{ key: string; label: string }> = [
    { key: "starts_at", label: "Başlangıç" },
    { key: "ends_at", label: "Bitiş" },
    { key: "instructor_id", label: "Eğitmen" },
    { key: "vehicle_id", label: "Araç" },
    { key: "student_id", label: "Kursiyer" },
    { key: "lesson_type", label: "Ders tipi" },
    { key: "status", label: "Durum" },
    { key: "cancel_reason_code", label: "İptal sebebi" },
    { key: "cancel_reason_note", label: "İptal notu" },
    { key: "conflict_accepted", label: "Çakışma istisnası" },
    { key: "conflict_reason", label: "İstisna gerekçesi" },
  ];

  const fmt = (key: string, val: unknown) => formatRefValue(key, val, labels);

  for (const field of fields) {
    const beforeValue = beforeRec ? beforeRec[field.key] : undefined;
    const afterValue = afterRec ? afterRec[field.key] : undefined;

    if (beforeValue === undefined && afterValue === undefined) continue;
    if (JSON.stringify(beforeValue) === JSON.stringify(afterValue)) continue;

    if (beforeValue === undefined) {
      lines.push(`${field.label}: ${fmt(field.key, afterValue)}`);
    } else if (afterValue === undefined) {
      lines.push(`${field.label}: ${fmt(field.key, beforeValue)} kaldırıldı`);
    } else {
      lines.push(`${field.label}: ${fmt(field.key, beforeValue)} → ${fmt(field.key, afterValue)}`);
    }
  }

  return lines;
}

export function AuditHistoryModal({ appointmentId, onClose }: Props) {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api
      .get<AuditResponse>(`/appointments/${appointmentId}/audit`, undefined, {
        signal: controller.signal,
      })
      .then((response) => {
        setEntries(response.entries);
        setLabels(response.labels ?? {});
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "Geçmiş yüklenemedi.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [appointmentId]);

  return (
    <div className="kp-modal-backdrop" role="dialog" aria-modal="true">
      <div className="kp-modal kp-card kp-appt-audit">
        <div className="kp-modal__head">
          <div>
            <strong>Randevu Geçmişi</strong>
            <span>ID: {shortUuid(appointmentId)}</span>
          </div>
          <button className="kp-btn kp-btn--ghost" onClick={onClose}>
            Kapat
          </button>
        </div>

        {loading && <div className="kp-loading">Geçmiş yükleniyor...</div>}
        {error && <div className="kp-error">{error}</div>}

        {!loading && !error && entries && entries.length === 0 && (
          <div className="kp-empty">Bu randevu için henüz geçmiş kaydı yok.</div>
        )}

        {!loading && !error && entries && entries.length > 0 && (
          <ul className="kp-appt-audit__timeline">
            {entries.map((entry) => {
              const meta = ACTION_LABELS[entry.action_type] || {
                label: entry.action_type,
                tone: "info" as const,
              };
              const changes = describeChange(entry.before_data, entry.after_data, labels);
              const actorLabel =
                entry.actor_display_name?.trim() ||
                labels[entry.actor_user_id] ||
                shortUuid(entry.actor_user_id);
              return (
                <li key={entry.id} className={`kp-appt-audit__item kp-appt-audit__item--${meta.tone}`}>
                  <div className="kp-appt-audit__dot" />
                  <div className="kp-appt-audit__content">
                    <div className="kp-appt-audit__head">
                      <strong>{meta.label}</strong>
                      <span>{formatDateTime(entry.created_at)}</span>
                    </div>
                    <div className="kp-appt-audit__actor">Yapan kullanıcı: {actorLabel}</div>
                    {changes.length > 0 && (
                      <ul className="kp-appt-audit__changes">
                        {changes.map((line, idx) => (
                          <li key={idx}>{line}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
